import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import type { MintShareResponse } from '../../lib/types';

/**
 * "Share your photos" card on the order status page (screen 1g). Mints a share
 * link (POST /orders/:name/share, gated by the same status token as the page)
 * and presents it in a copy field, with the native share sheet on mobile.
 * Re-clicks are harmless — the worker returns the same link for 30 days.
 */
export function ShareCard({ orderName, statusToken }: { orderName: string; statusToken: string }) {
  const [minting, setMinting] = useState(false);
  const [share, setShare] = useState<MintShareResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  async function mint() {
    if (minting || share) return;
    setMinting(true);
    setFailed(false);
    try {
      setShare(await api.mintShare(orderName, statusToken));
    } catch {
      setFailed(true); // quiet — the rest of the status page keeps working
    } finally {
      setMinting(false);
    }
  }

  async function copy() {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(share.share_url);
      setCopied(true);
    } catch {
      inputRef.current?.select(); // clipboard blocked — select for manual copy
    }
  }

  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  async function nativeShare() {
    if (!share) return;
    try {
      await navigator.share({ title: 'Order prints from my photos', url: share.share_url });
    } catch {
      /* user closed the sheet */
    }
  }

  return (
    <div className="ost-card">
      <div className="ost-card__eyebrow">Share your photos</div>
      <div className="ost-card__body">
        Share these photos with friends and family so they can order their own prints. The link
        works for 30 days.
      </div>
      {share ? (
        <div className="ost-share__row">
          <input
            ref={inputRef}
            className="ost-share__url"
            readOnly
            value={share.share_url}
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Share link"
          />
          <button type="button" className="btn btn--secondary" onClick={() => void copy()}>
            {copied ? 'Copied ✓' : 'Copy'}
          </button>
          {canNativeShare && (
            <button type="button" className="btn btn--secondary" onClick={() => void nativeShare()}>
              Share…
            </button>
          )}
        </div>
      ) : (
        <div className="ost-share__row">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void mint()}
            disabled={minting}
          >
            {minting ? 'Creating link…' : 'Create share link'}
          </button>
          {failed && (
            <span className="ost-share__err" role="status">
              We couldn&rsquo;t create a link just now. Try again in a moment.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
